import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Search, X, Settings, ArrowDownToLine, ChevronDown, Phone, Mail, Info, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCustomerPromises, type GetCustomerPromisesParams } from "@/hooks/useCustomerPromises";
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences";
import { UniqueTableNamesForColumnConfiguration } from "@/config/unique-table-names-for-column-configuration";
import { getGlobalDataStore } from "@/lib/mock-data/global-data-store";
import { exportPromisesToExcel } from "@/lib/promise-export-utils";
import { useExportAll } from "@/hooks/useExportAll";
import { useMode } from "@/providers/ModeProvider";
import { Forms, type ColumnInput } from "@/types/export";
import { toast } from "sonner";
import type { PromiseToPay, CollectionCase } from "@/types/collections";
import { formatCurrency } from "@/lib/utils/formatters";
import { ScopeLabel } from "@/components/customers/scope-label";

/** Maps UI column IDs to backend sort field names */
const COLUMN_SORT_KEY_MAP: Record<string, string> = {
  promiseId: 'PromiseId',
  customerName: 'CustomerName',
  caseNumber: 'CaseNumber',
  promiseDate: 'PromiseDate',
  daysOverdue: 'DaysOverdue',
  amount: 'PromisedAmount',
  transactions: 'InvoiceCount',
  status: 'StatusName',
  method: 'ConfirmationMethodName',
  customerAccountNumber: 'CustomerAccountNumber',
  customerEmail: 'ContactEmail',
  customerPhone: 'ContactPhone',
  createdDate: 'CreatedAt',
  createdBy: 'CreatedByName',
  notes: 'Notes',
};

const DEFAULT_PROMISE_TAB_COLUMNS: ColumnDefinition[] = [
  { id: 'promiseId', label: 'Promise ID', visible: true, order: 0 },
  { id: 'customerName', label: 'Customer', visible: true, order: 1 },
  { id: 'caseNumber', label: 'Case Number', visible: true, order: 2 },
  { id: 'lastAction', label: 'Last Action', visible: true, order: 3 },
  { id: 'promiseDate', label: 'Promise Date', visible: true, order: 4 },
  { id: 'daysOverdue', label: 'Days Overdue', visible: true, order: 5 },
  { id: 'amount', label: 'Amount', visible: true, order: 6 },
  { id: 'transactions', label: 'Transactions', visible: true, order: 7 },
  { id: 'status', label: 'Status', visible: true, order: 8 },
  { id: 'method', label: 'Method', visible: true, order: 9 },
  { id: 'customerAccountNumber', label: 'Account Number', visible: false, order: 10 },
  { id: 'customerEmail', label: 'Email', visible: false, order: 11 },
  { id: 'customerPhone', label: 'Phone', visible: false, order: 12 },
  { id: 'createdDate', label: 'Created Date', visible: false, order: 13 },
  { id: 'createdBy', label: 'Created By', visible: false, order: 14 },
  { id: 'notes', label: 'Notes', visible: false, order: 15 },
];

// Map frontend column IDs to backend PromiseToPayList property names for export
const promisesExportColumnNameMap: Record<string, string> = {
  promiseId: "PromiseId",
  customerName: "CustomerName",
  customerAccountNumber: "CustomerAccountNumber",
  caseNumber: "CaseNumber",
  lastAction: "LastActionType",
  promiseDate: "PromiseDate",
  daysOverdue: "DaysOverdue",
  amount: "PromisedAmount",
  transactions: "InvoiceCount",
  status: "StatusName",
  method: "ConfirmationMethodName",
  customerEmail: "ContactEmail",
  customerPhone: "ContactPhone",
  createdDate: "CreatedAt",
  createdBy: "CreatedByName",
  notes: "Notes",
  companyName: "CompanyName",
};

interface PromisesTabProps {
  customerId: string;
  customerName: string;
  accountNumber?: string;
  isConsolidatedView?: boolean;
  isMultiCompanyGroup?: boolean;
}

export function PromisesTab({ customerId, customerName, accountNumber, isConsolidatedView = false, isMultiCompanyGroup = false }: PromisesTabProps) {
  const navigate = useNavigate();
  const { isDemo } = useMode();
  
  // State
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [sortBy, setSortBy] = useState('PromiseDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search input (1 second delay)
  useEffect(() => {
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 1000);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  // Reset pagination and selections when consolidated view changes
  useEffect(() => {
    setPage(1);
    setSelectedRows(new Set());
    setSearchQuery("");
  }, [isConsolidatedView]);

  // Build query params for server-side pagination/sorting/filtering
  const queryParams: GetCustomerPromisesParams = useMemo(() => ({
    page,
    pageSize,
    sortBy,
    sortOrder,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    allCompanies: isConsolidatedView || undefined,
  }), [page, pageSize, sortBy, sortOrder, debouncedSearch, statusFilter, isConsolidatedView]);

  const { promises: rawPromises, pagination, isLoading } = useCustomerPromises(customerId, queryParams);

  // Reset to page 1 when filters change
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleStatusFilterChange = useCallback((value: string) => {
    setStatusFilter(value);
    setPage(1);
  }, []);

  const handleSort = useCallback((columnId: string) => {
    const backendKey = COLUMN_SORT_KEY_MAP[columnId];
    if (!backendKey) return;
    if (sortBy === backendKey) {
      setSortOrder(x => x === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(backendKey);
      setSortOrder('asc');
    }
    setPage(1);
  }, [sortBy]);

  const getSortIcon = useCallback((columnId: string) => {
    const backendKey = COLUMN_SORT_KEY_MAP[columnId];
    if (!backendKey) return null;
    if (sortBy !== backendKey) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortOrder === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  }, [sortBy, sortOrder]);
  
  // Column preferences
  const { columns, savePreferences, resetToDefaults } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.CustomerDetailPromises, DEFAULT_PROMISE_TAB_COLUMNS);
  
  // Auto-update promises with past promise dates to 'Broken' status
  const filteredPromises = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return rawPromises.map(promise => {
      const promiseDate = new Date(promise.promiseDate);
      promiseDate.setHours(0, 0, 0, 0);

      if (promiseDate < today && promise.status === 'Active') {
        const daysOverdue = Math.ceil((today.getTime() - promiseDate.getTime()) / (1000 * 60 * 60 * 24));
        return {
          ...promise,
          status: 'Broken' as const,
          daysOverdue,
        };
      }

      return promise;
    });
  }, [rawPromises]);
  
  // Get visible columns in order
  const visibleColumns = useMemo(() => {
    return columns
      .filter(col => col.visible)
      .sort((a, b) => a.order - b.order);
  }, [columns]);
  
  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(new Set(filteredPromises.map(p => p.id)));
    } else {
      setSelectedRows(new Set());
    }
  };
  
  const handleSelectRow = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedRows(newSelected);
  };
  
  const isAllSelected = filteredPromises.length > 0 && selectedRows.size === filteredPromises.length;
  const isSomeSelected = selectedRows.size > 0 && selectedRows.size < filteredPromises.length;
  
  // Action handlers
  const handleViewDetails = (promise: PromiseToPay) => {
    navigate(`/collections/promises/${promise.id}`);
  };
  
  const handleCaseNumberClick = (caseNumber: string) => {
    // Navigate to full-screen case details page
    navigate(`/collections/queue/case/${caseNumber}`, {
      state: { from: "customer-promises" }
    });
  };
  
  // Build visible columns for export
  const getPromisesExportColumns = useCallback((): ColumnInput[] => {
    return visibleColumns
      .filter((col) => col.id in promisesExportColumnNameMap)
      .map((col) => ({
        name: promisesExportColumnNameMap[col.id],
        displayName: col.label,
      }));
  }, [visibleColumns]);

  // Build all columns for export
  const getPromisesAllExportColumns = useCallback((): ColumnInput[] => {
    return DEFAULT_PROMISE_TAB_COLUMNS
      .filter((col) => col.id in promisesExportColumnNameMap)
      .map((col) => ({
        name: promisesExportColumnNameMap[col.id],
        displayName: col.label,
      }));
  }, []);

  // Build filter for promises export
  const getPromisesExportFilter = useCallback((): Record<string, unknown> | undefined => {
    const filter: Record<string, unknown> = {};
    if (customerId) filter.customerId = { eq: Number(customerId) };
    if (debouncedSearch) filter.summary = debouncedSearch;
    if (statusFilter !== 'all') filter.statusName = { eq: statusFilter };
    if (isConsolidatedView) filter.allCompanies = true;
    return Object.keys(filter).length > 0 ? filter : undefined;
  }, [customerId, debouncedSearch, statusFilter, isConsolidatedView]);

  // Build filter for selected rows export
  const getPromisesSelectedExportFilter = useCallback((): Record<string, unknown> | undefined => {
    if (selectedRows.size === 0) return undefined;
    const filter: Record<string, unknown> = {};
    filter.promiseId = { containsValues: Array.from(selectedRows).map(Number) };
    if (isConsolidatedView) filter.allCompanies = true;
    return filter;
  }, [selectedRows, isConsolidatedView]);

  // Server-side export hooks — main table
  const { triggerExportAll: triggerPromisesCurrentView } = useExportAll({
    form: Forms.PromisesToPay,
    getColumns: getPromisesExportColumns,
    getFilter: getPromisesExportFilter,
    fileName: `promises-${customerName}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const { triggerExportAll: triggerPromisesAllColumns } = useExportAll({
    form: Forms.PromisesToPay,
    getColumns: getPromisesAllExportColumns,
    getFilter: getPromisesExportFilter,
    fileName: `promises-${customerName}-all-columns-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  // Server-side export hooks — selected rows
  const { triggerExportAll: triggerPromisesSelectedCurrentView } = useExportAll({
    form: Forms.PromisesToPay,
    getColumns: getPromisesExportColumns,
    getFilter: getPromisesSelectedExportFilter,
    fileName: `selected-promises-${customerName}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const { triggerExportAll: triggerPromisesSelectedAllColumns } = useExportAll({
    form: Forms.PromisesToPay,
    getColumns: getPromisesAllExportColumns,
    getFilter: getPromisesSelectedExportFilter,
    fileName: `selected-promises-${customerName}-all-columns-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  // Demo mode client-side export handler - current view
  const handleDemoExportCurrentView = useCallback(() => {
    try {
      exportPromisesToExcel(filteredPromises);
      toast.success(`Exported ${filteredPromises.length} promise(s) to Excel`);
    } catch (error) {
      toast.error('Failed to export promises');
      console.error('Export error:', error);
    }
  }, [filteredPromises]);

  // Demo mode client-side export handler - all columns
  const handleDemoExportAllColumns = useCallback(() => {
    try {
      exportPromisesToExcel(filteredPromises, `customer-${customerId}-promises-all-columns-${new Date().toISOString().split('T')[0]}.csv`);
      toast.success(`Exported ${filteredPromises.length} promise(s) with all columns to Excel`);
    } catch (error) {
      toast.error('Failed to export promises');
      console.error('Export error:', error);
    }
  }, [filteredPromises, customerId]);

  // Mode-aware export handlers — main table
  const handleExportCurrentView = useCallback(() => {
    if (isDemo) {
      handleDemoExportCurrentView();
    } else {
      triggerPromisesCurrentView();
      toast.info('Export started. You will be notified when the file is ready.');
    }
  }, [isDemo, handleDemoExportCurrentView, triggerPromisesCurrentView]);

  const handleExportAllColumns = useCallback(() => {
    if (isDemo) {
      handleDemoExportAllColumns();
    } else {
      triggerPromisesAllColumns();
      toast.info('Export started. You will be notified when the file is ready.');
    }
  }, [isDemo, handleDemoExportAllColumns, triggerPromisesAllColumns]);

  // Mode-aware export handlers — selected rows
  const handleSelectedExportCurrentView = useCallback(() => {
    if (isDemo) {
      handleDemoExportCurrentView();
    } else {
      triggerPromisesSelectedCurrentView();
      toast.info('Export started. You will be notified when the file is ready.');
    }
  }, [isDemo, handleDemoExportCurrentView, triggerPromisesSelectedCurrentView]);

  const handleSelectedExportAllColumns = useCallback(() => {
    if (isDemo) {
      handleDemoExportAllColumns();
    } else {
      triggerPromisesSelectedAllColumns();
      toast.info('Export started. You will be notified when the file is ready.');
    }
  }, [isDemo, handleDemoExportAllColumns, triggerPromisesSelectedAllColumns]);
  
  const handleBulkEmail = () => {
    if (selectedRows.size === 1) {
      const promiseId = Array.from(selectedRows)[0];
      const promise = filteredPromises.find(p => p.id === promiseId);
      if (promise) {
        toast.info(`Opening email for ${promise.customerName}`);
      }
    } else {
      toast.success(`Email sent to ${selectedRows.size} customers`);
      setSelectedRows(new Set());
    }
  };
  
  const handleBulkCall = () => {
    if (selectedRows.size === 1) {
      const promiseId = Array.from(selectedRows)[0];
      const promise = filteredPromises.find(p => p.id === promiseId);
      if (promise) {
        toast.info(`Logging call for ${promise.customerName}`);
      }
    } else {
      toast.info(`Logging calls for ${selectedRows.size} customers`);
    }
  };
  
  const handleClearSelection = () => {
    setSelectedRows(new Set());
  };
  
  // Badge helpers
  const getStatusBadge = (status: PromiseToPay['status']) => {
    const variants: Record<PromiseToPay['status'], { className: string }> = {
      Active: { className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
      Broken: { className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
      Paid: { className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
      'Partially Paid': { className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
      Closed: { className: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
    };
    return <Badge className={variants[status].className}>{status}</Badge>;
  };
  
  const getConfirmationMethodBadge = (method: PromiseToPay['confirmationMethod']) => {
    const icons = {
      Phone: <Phone className="h-3 w-3 mr-1" />,
      Email: <Mail className="h-3 w-3 mr-1" />,
      Portal: <Info className="h-3 w-3 mr-1" />,
    };
    return (
      <Badge variant="outline" className="flex items-center gap-1">
        {icons[method]}
        {method}
      </Badge>
    );
  };
  
  // Helper function to render table cell content based on column ID
  const renderTableCell = (columnId: string, promise: PromiseToPay) => {
    switch (columnId) {
      case 'companyName':
        return promise.companyName ?? promise.companyId ?? '-';
      case 'select':
        return (
          <Checkbox
            checked={selectedRows.has(promise.id)}
            onCheckedChange={(checked) => handleSelectRow(promise.id, checked as boolean)}
            aria-label={`Select promise ${promise.id}`}
          />
        );
      case 'promiseId':
        return (
          <button
            onClick={() => handleViewDetails(promise)}
            className="text-primary hover:underline hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded cursor-pointer transition-colors"
          >
            {promise.id}
          </button>
        );
      case 'customerName':
        return (
          <span className="font-medium">
            {promise.customerName}
          </span>
        );
      case 'customerAccountNumber':
        return <span>{promise.customerAccountNumber}</span>;
      case 'caseNumber':
        return promise.caseNumber ? (
          <button
            onClick={() => handleCaseNumberClick(promise.caseNumber)}
            className="text-primary hover:underline hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded cursor-pointer transition-colors font-medium"
          >
            {promise.caseNumber}
          </button>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      case 'lastAction':
        return promise.lastAction ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
                  <Info className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <div className="space-y-1">
                  <p className="font-semibold text-sm capitalize">{promise.lastAction.type.replace('_', ' ')}</p>
                  <p className="text-xs">{promise.lastAction.description}</p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t">
                    <span>{promise.lastAction.performedBy}</span>
                    <span>{format(promise.lastAction.date, 'dd/MM/yyyy')}</span>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      case 'promiseDate':
        return <span>{format(promise.promiseDate, 'dd/MM/yyyy')}</span>;
      case 'daysOverdue':
        return promise.daysOverdue && promise.daysOverdue > 0 ? (
          <span className="text-red-600 font-medium">{promise.daysOverdue}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      case 'amount':
        return <div className="font-medium">{formatCurrency(promise.promisedAmount)}</div>;
      case 'transactions':
        return <div className="text-sm">{promise.invoices.length}</div>;
      case 'status':
        return getStatusBadge(promise.status);
      case 'method':
        return getConfirmationMethodBadge(promise.confirmationMethod);
      case 'customerEmail':
        return <span className="text-sm">{promise.customerEmail || '-'}</span>;
      case 'customerPhone':
        return <span className="text-sm">{promise.customerPhone || '-'}</span>;
      case 'createdDate':
        return <span className="text-sm">{promise.createdAt ? format(new Date(promise.createdAt), 'dd/MM/yyyy') : '-'}</span>;
      case 'createdBy':
        return <span className="text-sm">{promise.createdBy || '-'}</span>;
      case 'notes':
        return <span className="text-sm truncate max-w-xs">{promise.notes || '-'}</span>;
      default:
        return null;
    }
  };
  
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex flex-col">
            <CardTitle className="text-base font-medium">
              Promise to Pay Records
              <ScopeLabel isMultiCompanyGroup={isMultiCompanyGroup} isConsolidatedView={isConsolidatedView} />{" "}
              <span className="text-sm font-normal text-muted-foreground">({pagination.totalItems})</span>
            </CardTitle>
            <CardDescription>
              Payment promises for {customerName}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-1 ml-4 justify-end">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by promise ID, amount..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-8"
              />
              {searchQuery && (
                <button
                  onClick={() => handleSearchChange("")}
                  className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Broken">Broken</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="Partially Paid">Partially Paid</SelectItem>
              </SelectContent>
            </Select>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filteredPromises.length === 0}
                  className="h-9"
                  title="Export to Excel"
                >
                  <ArrowDownToLine className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportCurrentView}>
                  Export Current View
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportAllColumns}>
                  Export All Columns
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setColumnConfigOpen(true)}
                    aria-label="Configure Columns"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Configure Columns</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardHeader>
        
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company Name</TableHead>
                {visibleColumns.map((column) => (
                  <TableHead 
                    key={column.id}
                    className={column.id === 'select' ? 'w-12' : (COLUMN_SORT_KEY_MAP[column.id] ? 'cursor-pointer select-none' : '')}
                    onClick={COLUMN_SORT_KEY_MAP[column.id] ? () => handleSort(column.id) : undefined}
                  >
                    {column.id === 'select' ? (
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all promises"
                        className={isSomeSelected ? "data-[state=checked]:bg-primary/50" : ""}
                      />
                    ) : (
                      <span className="inline-flex items-center">
                        {column.label}
                        {getSortIcon(column.id)}
                      </span>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 1} className="h-64 text-center">
                    <div className="flex flex-col items-center justify-center space-y-4">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <p className="text-sm text-muted-foreground">Loading promises...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredPromises.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 1} className="text-center text-muted-foreground py-8">
                    {searchQuery || statusFilter !== 'all' 
                      ? 'No promises found matching your criteria'
                      : 'No promises recorded for this customer'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredPromises.map((promise) => {
                  const isSelected = selectedRows.has(promise.id);
                  return (
                    <TableRow 
                      key={promise.id}
                      className={isSelected ? "bg-muted/50" : ""}
                    >
                      <TableCell>{renderTableCell('companyName', promise)}</TableCell>
                      {visibleColumns.map((column) => (
                        <TableCell key={column.id}>
                          {renderTableCell(column.id, promise)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Pagination Controls */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between px-2">
          <p className="text-sm text-muted-foreground">
            Showing {((pagination.page - 1) * pagination.pageSize) + 1}-{Math.min(pagination.page * pagination.pageSize, pagination.totalItems)} of {pagination.totalItems}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(x => Math.max(1, x - 1))}
              disabled={!pagination.hasPreviousPage}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(x => x + 1)}
              disabled={!pagination.hasNextPage}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onApply={savePreferences}
        onReset={resetToDefaults}
      />
      
      {/* Floating Actions Bar */}
      {selectedRows.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
          <div className="bg-primary text-primary-foreground rounded-lg shadow-2xl border border-primary/20">
            <div className="flex items-center gap-4 px-6 py-4">
              {/* Selection Info */}
              <div className="flex items-center gap-3 border-r border-primary-foreground/20 pr-4">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30">
                      {selectedRows.size}
                    </Badge>
                    <span className="font-medium">
                      {selectedRows.size === 1 ? "promise" : "promises"} selected
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="sm" className="gap-2">
                      <ArrowDownToLine className="h-4 w-4" />
                      Export
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleSelectedExportCurrentView}>
                      Export Current View
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleSelectedExportAllColumns}>
                      Export All Columns
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleBulkEmail}
                  className="gap-2"
                >
                  <Mail className="h-4 w-4" />
                  Send Email
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleBulkCall}
                  className="gap-2"
                >
                  <Phone className="h-4 w-4" />
                  Log Call
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSelection}
                  className="gap-2 hover:bg-primary-foreground/20"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
